// AI Voortgang — alles wat met reacties van de klant te maken heeft: meerwerk-verzoeken
// (offerte sturen / afwijzen), opmerkingen beantwoorden en de inbox over alle projecten.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, Switch, TextField, Typography,
} from '@mui/material';
import { bevestig } from '../../utils/bevestig';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { meldFout } from '../../utils/apiFout';
import {
  ACCENT, INKT, LIJN, STIL, VERZOEK_STATUS, datumTijd, euro, haalInbox, plaatsOpmerking, stuurOfferte, wijsVerzoekAf,
  zetOpgelost,
} from './voortgangApi';

function Chip({ s }) {
  return (
    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 10, fontSize: 12, fontWeight: 600, color: s.kleur, bgcolor: s.bg, whiteSpace: 'nowrap' }}>
      {s.label}
    </Box>
  );
}

// ── Meerwerk: extra ronde / terug naar een afgeronde fase ──────────────────

export function Verzoeken({ verzoeken, canEdit, onProject }) {
  const [offerte, setOfferte] = useState(null); // { verzoek, bedrag, toelichting }
  const [toonAlles, setToonAlles] = useState(false);
  const open = (verzoeken || []).filter((v) => ['requested', 'quoted'].includes(v.status));
  const rest = (verzoeken || []).filter((v) => !['requested', 'quoted'].includes(v.status));
  if (!open.length && !rest.length) return null;

  async function verstuur() {
    const bedrag = Math.round(Number(String(offerte.bedrag).replace(',', '.')) * 100);
    if (!Number.isFinite(bedrag) || bedrag < 0) { meldFout(new Error('Vul een geldig bedrag in (0 mag)'), 'Offerte'); return; }
    const { verzoek, toelichting } = offerte;
    setOfferte(null);
    const r = await voerUitgesteldUit(`Offerte van ${euro(bedrag)} naar de klant…`,
      () => stuurOfferte(verzoek.id, bedrag, toelichting.trim()), { duur: 5000, klaarTekst: 'Offerte verstuurd' });
    if (!r.geannuleerd && !r.fout) onProject(r.resultaat.project);
  }

  async function afwijzen(verzoek) {
    const ok = await bevestig({
      titel: 'Verzoek afwijzen?',
      tekst: verzoek.soort === 'reopen'
        ? 'De fase blijft afgerond. De klant ziet dat het verzoek is afgewezen.'
        : 'Er komt geen extra ronde; de klant kan opnieuw beslissen over de gedeelde versie.',
      knop: 'Afwijzen', gevaarlijk: true,
    });
    if (!ok) return;
    try { onProject((await wijsVerzoekAf(verzoek.id, '')).project); }
    catch (e) { meldFout(e, 'Verzoek afwijzen'); }
  }

  const rij = (v) => (
    <Box key={v.id} sx={{ py: 1.25, borderTop: `1px solid ${LIJN}`, '&:first-of-type': { borderTop: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: INKT, flex: 1, minWidth: 0 }}>
          {v.soort_naam} · {v.fase}{v.item ? ` / ${v.item}` : ''}
        </Typography>
        {v.bedrag_cents !== null && v.bedrag_cents !== undefined && <Typography sx={{ fontSize: 13, fontWeight: 700, color: INKT }}>{euro(v.bedrag_cents)}</Typography>}
        <Chip s={VERZOEK_STATUS[v.status] || VERZOEK_STATUS.requested} />
      </Box>
      <Typography sx={{ fontSize: 12, color: STIL }}>
        {v.door || 'Klant'} · {datumTijd(v.op)}
        {v.geraakte_fases?.length ? ` · gaat ook terug: ${v.geraakte_fases.join(', ')}` : ''}
      </Typography>
      {v.toelichting && <Typography sx={{ fontSize: 13, color: INKT, whiteSpace: 'pre-wrap', mt: 0.25 }}>{v.toelichting}</Typography>}
      {v.offerte_toelichting && <Typography sx={{ fontSize: 12.5, color: STIL, mt: 0.25 }}>Offerte: {v.offerte_toelichting}</Typography>}
      {canEdit && v.status === 'requested' && (
        <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
          <Button size="small" variant="contained" disableElevation sx={{ textTransform: 'none', bgcolor: ACCENT }}
            onClick={() => setOfferte({ verzoek: v, bedrag: '', toelichting: '' })}>Offerte sturen</Button>
          <Button size="small" sx={{ textTransform: 'none', color: STIL }} onClick={() => afwijzen(v)}>Afwijzen</Button>
        </Box>
      )}
      {canEdit && v.status === 'quoted' && (
        <Button size="small" sx={{ textTransform: 'none', color: STIL, mt: 0.5 }} onClick={() => afwijzen(v)}>Offerte intrekken</Button>
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 2, border: `1px solid ${open.length ? '#fecaca' : LIJN}`, borderRadius: 2, bgcolor: '#fff', px: 2, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: INKT, flex: 1 }}>
          Meerwerk{open.length ? ` · ${open.length} open` : ''}
        </Typography>
        {rest.length > 0 && (
          <Button size="small" onClick={() => setToonAlles((x) => !x)} sx={{ textTransform: 'none', color: STIL }}>
            {toonAlles ? 'Verberg afgehandeld' : `Afgehandeld (${rest.length})`}
          </Button>
        )}
      </Box>
      {open.map(rij)}
      {toonAlles && rest.map(rij)}

      <Dialog open={!!offerte} onClose={() => setOfferte(null)} fullWidth maxWidth="xs">
        <DialogTitle>Offerte voor {offerte?.verzoek.soort_naam.toLowerCase()}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography sx={{ fontSize: 13, color: STIL }}>
            De klant krijgt dit te zien en kan accepteren of afwijzen.
            {offerte?.verzoek.soort === 'reopen' ? ' Na acceptatie gaat de fase direct weer open.' : ' Pas na acceptatie kun je de nieuwe ronde delen.'}
          </Typography>
          <TextField size="small" autoFocus label="Bedrag excl. btw" value={offerte?.bedrag || ''} placeholder="0"
            onChange={(e) => setOfferte((o) => ({ ...o, bedrag: e.target.value }))} helperText="€ 0 mag: dan is het kosteloos"
            InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} inputProps={{ inputMode: 'decimal' }} />
          <TextField size="small" multiline minRows={2} label="Toelichting (optioneel)" value={offerte?.toelichting || ''}
            inputProps={{ maxLength: 2000 }} onChange={(e) => setOfferte((o) => ({ ...o, toelichting: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferte(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disableElevation onClick={verstuur} disabled={String(offerte?.bedrag || '').trim() === ''}
            sx={{ textTransform: 'none', bgcolor: ACCENT }}>Naar de klant sturen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Opmerkingen per onderdeel ──────────────────────────────────────────────

function tijdcode(ms) {
  const s = Math.floor((ms || 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function Opmerkingen({ item, versies, canEdit, onProject }) {
  const [antwoord, setAntwoord] = useState({}); // draadId → tekst
  const [nieuw, setNieuw] = useState('');
  const [bezig, setBezig] = useState(false);
  const draden = item.opmerkingen || [];
  const versieNr = Object.fromEntries((versies || []).map((v) => [v.id, v.versie]));

  async function plaats(tekst, parentId) {
    if (!tekst.trim() || bezig) return false;
    setBezig(true);
    try { onProject((await plaatsOpmerking(item.id, tekst.trim(), parentId)).project); return true; }
    catch (e) { meldFout(e, 'Opmerking plaatsen'); return false; }
    finally { setBezig(false); }
  }

  async function opgelost(d) {
    try { onProject((await zetOpgelost(d.id, !d.opgelost)).project); }
    catch (e) { meldFout(e, 'Opmerking bijwerken'); }
  }

  if (!draden.length && !canEdit) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {draden.map((d) => (
        <Box key={d.id} sx={{ mt: 0.75, p: 1, borderRadius: 1.5, bgcolor: d.opgelost ? '#f9fafb' : '#f8fafc', border: `1px solid ${LIJN}`, opacity: d.opgelost ? 0.7 : 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: INKT, flex: 1 }}>
              {d.door} · {datumTijd(d.op)}
              {d.versie_id && versieNr[d.versie_id] ? <span style={{ color: STIL, fontWeight: 500 }}> · bij v{versieNr[d.versie_id]}</span> : ''}
              {d.plek ? <span style={{ color: STIL, fontWeight: 500 }}>
                {d.plek.tijd_ms !== null && d.plek.tijd_ms !== undefined ? ` · op ${tijdcode(d.plek.tijd_ms)}` : ''} · plek {Math.round(d.plek.x * 100)}%, {Math.round(d.plek.y * 100)}%
              </span> : ''}
            </Typography>
            {canEdit && (
              <Button size="small" onClick={() => opgelost(d)} sx={{ textTransform: 'none', color: STIL, minWidth: 0, py: 0 }}>
                {d.opgelost ? 'Heropenen' : '✓ Opgelost'}
              </Button>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: INKT, whiteSpace: 'pre-wrap' }}>{d.tekst}</Typography>
          {(d.antwoorden || []).map((a) => (
            <Box key={a.id} sx={{ mt: 0.5, pl: 1.5, borderLeft: `2px solid ${LIJN}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: INKT }}>{a.door} · {datumTijd(a.op)}</Typography>
              <Typography sx={{ fontSize: 13, color: INKT, whiteSpace: 'pre-wrap' }}>{a.tekst}</Typography>
            </Box>
          ))}
          {canEdit && !d.opgelost && (
            <TextField size="small" fullWidth placeholder="Antwoord aan de klant… (Enter)" value={antwoord[d.id] || ''} sx={{ mt: 0.75 }}
              inputProps={{ 'aria-label': 'Antwoord', maxLength: 5000 }} disabled={bezig}
              onChange={(e) => setAntwoord((x) => ({ ...x, [d.id]: e.target.value }))}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (await plaats(antwoord[d.id] || '', d.id)) setAntwoord((x) => ({ ...x, [d.id]: '' }));
                }
              }} />
          )}
        </Box>
      ))}
      {canEdit && (
        <TextField size="small" fullWidth placeholder="Opmerking voor de klant bij dit onderdeel… (Enter)" value={nieuw} sx={{ mt: 0.75 }}
          inputProps={{ 'aria-label': 'Nieuwe opmerking', maxLength: 5000 }} disabled={bezig}
          onChange={(e) => setNieuw(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (await plaats(nieuw)) setNieuw(''); }
          }} />
      )}
    </Box>
  );
}

// ── Inbox over alle projecten ──────────────────────────────────────────────

const SOORTEN = {
  verzoek: { label: 'Offerte nodig', kleur: '#b91c1c', bg: '#fee2e2' },
  opmerking: { label: 'Opmerking', kleur: '#1e40af', bg: '#e0e7ff' },
  wijziging: { label: 'Nieuwe versie nodig', kleur: '#b91c1c', bg: '#fee2e2' },
  offerte: { label: 'Offerte bij klant', kleur: '#b45309', bg: '#fef3c7' },
  wacht: { label: 'Wacht op klant', kleur: '#b45309', bg: '#fef3c7' },
  akkoord: { label: 'Akkoord', kleur: '#15803d', bg: '#dcfce7' },
};

function wachtTekst(r) {
  if (r.soort === 'wacht' && r.dagen) return `${r.tekst} · ${r.dagen} dag${r.dagen === 1 ? '' : 'en'}`;
  if (r.soort === 'offerte') return `${r.verzoek_soort} · ${euro(r.bedrag_cents)}`;
  if (r.soort === 'verzoek') return `${r.verzoek_soort}${r.tekst ? ` — ${r.tekst}` : ''}`;
  return r.tekst;
}

export function Inbox({ onOpen }) {
  const [alleenMijn, setAlleenMijn] = useState(() => {
    try { return localStorage.getItem('voortgang.inbox.mijn') === '1'; } catch { return false; }
  });
  const [data, setData] = useState(null);

  const laad = useCallback(async () => {
    setData(null);
    try { setData(await haalInbox(alleenMijn)); }
    catch (e) { meldFout(e, 'Inbox laden'); setData({ regels: [], tellers: {} }); }
  }, [alleenMijn]);
  useEffect(() => { laad(); }, [laad]);

  function wissel(v) {
    setAlleenMijn(v);
    try { localStorage.setItem('voortgang.inbox.mijn', v ? '1' : '0'); } catch { /* geen opslag: geen probleem */ }
  }

  const actie = (data?.regels || []).filter((r) => ['verzoek', 'opmerking', 'wijziging'].includes(r.soort));
  const overig = (data?.regels || []).filter((r) => !['verzoek', 'opmerking', 'wijziging'].includes(r.soort));
  const lijst = (titel, regels, leeg) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: STIL, mb: 0.75 }}>{titel}</Typography>
      {regels.length === 0 ? <Typography sx={{ fontSize: 13.5, color: STIL }}>{leeg}</Typography> : regels.map((r, i) => (
        <ButtonBase key={`${r.soort}-${r.ref || r.fase_id}-${i}`} onClick={() => onOpen(r.project_id, r.item_id)}
          sx={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 1.5, px: 1.25, py: 1,
            borderRadius: 1.5, borderTop: `1px solid ${LIJN}`, '&:hover': { bgcolor: '#f3f4f6' } }}>
          <Box sx={{ width: 150, flexShrink: 0 }}><Chip s={SOORTEN[r.soort]} /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INKT }}>
              {[r.project_number, r.project].filter(Boolean).join(' · ')}
              <span style={{ color: STIL, fontWeight: 500 }}> — {[r.fase, r.item].filter(Boolean).join(' / ')}</span>
            </Typography>
            <Typography noWrap sx={{ fontSize: 12.5, color: STIL }}>
              {[r.klant, r.door, wachtTekst(r)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: STIL, whiteSpace: 'nowrap' }}>{datumTijd(r.op)}</Typography>
        </ButtonBase>
      ))}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: INKT, flex: 1 }}>Inbox</Typography>
        <FormControlLabel control={<Switch size="small" checked={alleenMijn} onChange={(e) => wissel(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13, color: STIL }}>Alleen mijn projecten</Typography>} />
      </Box>
      {data === null ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box> : (
        <>
          {lijst('Voor Full Frame', actie, 'Niets dat op jou wacht.')}
          {lijst('Bij de klant en recent', overig, 'Niets onderweg.')}
        </>
      )}
    </Box>
  );
}
